import React from "react"
import { StyleSheet, Text, View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { theme } from "@common/theme/theme"

/**
 * Blocs de présentation du checkout des funnels d'acquisition — extraits de
 * l'écran Cours d'été, qui les a mis au point, pour que tout funnel affiche le
 * MÊME récap et le MÊME bloc de prix.
 *
 * Ce fichier ne porte que la forme. Le contenu (quelles lignes, quels montants,
 * quelles échéances) reste à l'écran appelant : chaque funnel vend autre chose,
 * et c'est justement cette partie-là qui doit diverger, pas la carte.
 *
 * Règle en cas d'évolution : un ajustement visuel se fait ICI, une fois. Si
 * un funnel a besoin d'une variante, elle passe par une prop, pas par une
 * copie du bloc dans l'écran.
 */

/** Une ligne du récap : icône, libellé court, valeur. */
export const FunnelRecapLine = ({ icon, label, value, numberOfLines = 2 }) => (
    <View style={styles.recapLine}>
        <FontAwesome5
            name={icon}
            solid
            size={11}
            color={theme.colors.grey}
            style={styles.recapIcon}
        />
        <Text style={styles.recapLineLabel}>{label}</Text>
        <Text style={styles.recapLineValue} numberOfLines={numberOfLines}>
            {value}
        </Text>
    </View>
)

/**
 * Carte « Votre sélection » — ce que le parent a composé, récapitulé juste
 * avant de payer. `onEdit` affiche le lien « Modifier » ; sans lui, le récap
 * est en lecture seule.
 */
export const FunnelRecapCard = ({
    title = "Votre sélection",
    onEdit,
    editLabel = "Modifier",
    children,
}) => (
    <View style={styles.recapCard}>
        <View style={styles.recapHeader}>
            <Text style={styles.recapTitle}>{title}</Text>
            {onEdit ? (
                <Text
                    style={styles.recapEdit}
                    onPress={onEdit}
                    accessibilityRole="button"
                >
                    {editLabel}
                </Text>
            ) : null}
        </View>
        {children}
    </View>
)

/**
 * Bloc de prix : le montant du jour domine, les échéances suivantes se lisent
 * dessous. Les détails (date de démarrage, modalités) passent en `children`,
 * séparés par un filet.
 */
export const FunnelPriceCard = ({
    todayLabel = "Aujourd'hui",
    todayValue,
    thenLines = [],
    children,
}) => (
    <View style={styles.priceCard}>
        <Text style={styles.heroTodayLabel}>{todayLabel}</Text>
        <Text style={styles.heroTodayValue}>{todayValue}</Text>
        {thenLines.filter(Boolean).map((line, index) => (
            <Text key={index} style={styles.heroThen}>
                {line}
            </Text>
        ))}
        {children ? (
            <>
                <View style={styles.priceDivider} />
                {children}
            </>
        ) : null}
    </View>
)

/** Une modalité sous le filet du bloc de prix (démarrage, visio, …). */
export const FunnelPriceDetail = ({ icon, children }) => (
    <View style={styles.priceDetailRow}>
        <FontAwesome5
            name={icon}
            size={12}
            color={theme.colors.grey}
            style={styles.priceDetailIcon}
        />
        <Text style={styles.priceDetailText}>{children}</Text>
    </View>
)

const styles = StyleSheet.create({
    recapCard: {
        borderRadius: 12,
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        backgroundColor: theme.colors.whiteAlmost,
        paddingVertical: 12,
        paddingHorizontal: 14,
        marginBottom: 16,
        gap: 8,
    },
    recapHeader: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 2,
    },
    recapTitle: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 13,
        color: theme.colors.primary,
    },
    recapEdit: {
        fontFamily: "Montserrat-Bold",
        fontSize: 12,
        color: theme.colors.primary,
        textDecorationLine: "underline",
    },
    recapLine: {
        flexDirection: "row",
        alignItems: "flex-start",
    },
    recapIcon: {
        width: 16,
        marginTop: 2,
        marginRight: 6,
    },
    recapLineLabel: {
        fontFamily: "Montserrat-Bold",
        fontSize: 12,
        color: theme.colors.grey,
        width: 72,
    },
    recapLineValue: {
        flex: 1,
        fontFamily: "Montserrat-Bold",
        fontSize: 12,
        color: theme.colors.primary,
        lineHeight: 17,
    },
    priceCard: {
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        borderRadius: 14,
        padding: 16,
        backgroundColor: theme.colors.whiteAlmost,
    },
    heroTodayLabel: {
        fontFamily: "Montserrat-Bold",
        fontSize: 13,
        color: theme.colors.grey,
        textAlign: "center",
    },
    heroTodayValue: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 44,
        lineHeight: 50,
        color: theme.colors.success || "#00BE89",
        textAlign: "center",
    },
    heroThen: {
        fontFamily: "Montserrat-Regular",
        fontSize: 13,
        color: theme.colors.grey,
        textAlign: "center",
        marginTop: 2,
    },
    priceDivider: {
        height: 1,
        backgroundColor: theme.colors.lightMore,
        marginVertical: 12,
    },
    priceDetailRow: {
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: 3,
    },
    priceDetailIcon: { width: 20 },
    priceDetailText: {
        flex: 1,
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        color: theme.colors.grey,
        lineHeight: 16,
    },
})
